import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import { Shield, AlertTriangle, CheckCircle, XCircle, Power } from 'lucide-react';

const API_URL = window.__BACKEND_URL;

export default function PostingSafety() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState(null);
  const [brands, setBrands] = useState([]);
  const [scheduledPostsCount, setScheduledPostsCount] = useState(0);

  useEffect(() => {
    loadSettings();
    loadBrands();
    loadScheduledPostsCount();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/posting-safety/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (err) {
      console.error('Error loading settings:', err);
      setError('Error loading settings: ' + (err.response?.data?.detail || err.message));
    }
  };

  const loadBrands = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/internal-brands`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBrands(response.data);
    } catch (err) {
      console.error('Error loading brands:', err);
    }
  };

  const loadScheduledPostsCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/scheduler/posts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScheduledPostsCount(response.data.length);
    } catch (err) {
      console.error('Error loading scheduled posts:', err);
    }
  };

  const toggleGlobalPosting = async () => {
    const newState = !settings?.posting_enabled;
    const confirmMessage = newState
      ? 'Are you sure you want to ENABLE posting? All paused posts will resume and can be published.'
      : 'Are you sure you want to DISABLE posting? All scheduled posts will be paused immediately.';
    
    if (!confirm(confirmMessage)) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/posting-safety/toggle?enabled=${newState}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`✅ ${response.data.message}`);
      await loadSettings();
      await loadScheduledPostsCount();
      setError(null);
    } catch (err) {
      setError('Error toggling posting: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const toggleBrandPosting = async (brandId) => {
    const currentState = settings?.brand_posting_enabled?.[brandId] || false;
    const newState = !currentState;
    const brand = brands.find(b => b.id === brandId);

    if (!confirm(`${newState ? 'Enable' : 'Disable'} posting for ${brand?.display_name}?`)) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/posting-safety/toggle?enabled=${newState}&brand_id=${brandId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadSettings();
      setError(null);
    } catch (err) {
      setError('Error toggling brand posting: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const isPostingEnabled = settings?.posting_enabled || false;

  return (
    <DashboardLayout>
      <div className="p-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-red-400 via-orange-400 to-yellow-400 bg-clip-text text-transparent mb-2">
            Posting Safety
          </h1>
          <p className="text-gray-400">Global control for all content posting</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-300">
            {error}
          </div>
        )}

        {/* Master Toggle */}
        <div className="mb-8">
          <div className={`p-8 rounded-lg border-2 ${
            isPostingEnabled
              ? 'bg-green-500/10 border-green-500/30'
              : 'bg-red-500/10 border-red-500/30'
          }`}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                  isPostingEnabled ? 'bg-green-500/20' : 'bg-red-500/20'
                }`}>
                  {isPostingEnabled ? (
                    <Power className="w-8 h-8 text-green-400" />
                  ) : (
                    <Shield className="w-8 h-8 text-red-400" />
                  )}
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-white mb-1">
                    Posting: {isPostingEnabled ? 'ENABLED' : 'DISABLED'}
                  </h2>
                  <p className={`text-lg ${isPostingEnabled ? 'text-green-300' : 'text-red-300'}`}>
                    {isPostingEnabled
                      ? 'Content can be published to social platforms'
                      : 'All posting engines are paused'}
                  </p>
                </div>
              </div>
              
              <button
                onClick={toggleGlobalPosting}
                disabled={loading}
                className={`px-8 py-4 rounded-lg font-bold text-lg transition-all disabled:opacity-50 ${
                  isPostingEnabled
                    ? 'bg-red-500 hover:bg-red-600 text-white'
                    : 'bg-green-500 hover:bg-green-600 text-white'
                }`}
              >
                {isPostingEnabled ? 'DISABLE Posting' : 'ENABLE Posting'}
              </button>
            </div>

            {/* Status Details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-6 border-t border-white/10">
              <div className="flex items-center gap-3">
                {isPostingEnabled ? (
                  <CheckCircle className="w-5 h-5 text-green-400" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-400" />
                )}
                <div>
                  <p className="text-sm text-gray-400">Posting Engine</p>
                  <p className="text-white font-medium">
                    {isPostingEnabled ? 'Active' : 'Disabled'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                {isPostingEnabled ? (
                  <CheckCircle className="w-5 h-5 text-green-400" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-400" />
                )}
                <div>
                  <p className="text-sm text-gray-400">Self-Marketing</p>
                  <p className="text-white font-medium">
                    {isPostingEnabled ? 'Can Post' : 'Cannot Post'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                {isPostingEnabled ? (
                  <CheckCircle className="w-5 h-5 text-green-400" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                )}
                <div>
                  <p className="text-sm text-gray-400">Scheduled Posts</p>
                  <p className="text-white font-medium">
                    {scheduledPostsCount} {isPostingEnabled ? 'Active' : 'Paused'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Warning Box */}
        {!isPostingEnabled && (
          <div className="mb-8 p-6 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-yellow-300 mb-2">
                  Posting is Currently Disabled
                </h3>
                <ul className="space-y-1 text-sm text-yellow-400/90">
                  <li>• Content can still be created and scheduled</li>
                  <li>• All scheduled posts are paused</li>
                  <li>• Self-Marketing engine will not publish content</li>
                  <li>• No content will be posted to social platforms</li>
                  <li>• Enable posting when you're ready to go live</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Per-Brand Toggles */}
        <div>
          <h2 className="text-2xl font-bold text-white mb-4">Brand-Specific Controls</h2>
          <p className="text-gray-400 mb-6">
            Fine-tune posting permissions per brand (requires global posting to be enabled)
          </p>

          <div className="space-y-4">
            {brands.map((brand) => {
              const brandEnabled = settings?.brand_posting_enabled?.[brand.id] ?? true;
              const effectivelyEnabled = isPostingEnabled && brandEnabled;

              return (
                <div
                  key={brand.id}
                  className="p-6 bg-white/5 backdrop-blur-md border border-white/10 rounded-lg"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: brand.primary_color_hex + '40' }}
                      >
                        <span className="text-2xl">{brand.slug === 'consistency_app' ? '🎯' : '💰'}</span>
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-white">{brand.display_name}</h3>
                        <p className="text-sm text-gray-400">
                          {effectivelyEnabled ? 'Posting enabled' : 'Posting disabled'}
                        </p>
                      </div>
                    </div>

                    <button
                      onClick={() => toggleBrandPosting(brand.id)}
                      disabled={loading || !isPostingEnabled}
                      className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        brandEnabled ? 'bg-green-500' : 'bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                          brandEnabled ? 'translate-x-7' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {!isPostingEnabled && (
                    <p className="mt-3 text-xs text-yellow-400/80">
                      Enable global posting first to control this brand
                    </p>
                  )}
                </div>
              );
            })}

            {brands.length === 0 && (
              <div className="text-center py-8 text-gray-400">
                No brands configured. Seed brands from Self-Marketing page.
              </div>
            )}
          </div>
        </div>

        {/* Safety Requirements */}
        <div className="mt-8 p-6 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <h3 className="text-lg font-semibold text-blue-300 mb-3">Safety Requirements</h3>
          <div className="space-y-2 text-sm text-blue-400/90">
            <div className="flex items-center gap-2">
              {settings?.require_permission_tests ? (
                <CheckCircle className="w-4 h-4 text-green-400" />
              ) : (
                <XCircle className="w-4 h-4 text-gray-400" />
              )}
              <span>Permission tests required before posting</span>
            </div>
            <div className="flex items-center gap-2">
              {settings?.require_manual_approval_first_post ? (
                <CheckCircle className="w-4 h-4 text-green-400" />
              ) : (
                <XCircle className="w-4 h-4 text-gray-400" />
              )}
              <span>Manual approval required for first post</span>
            </div>
          </div>
        </div>

        {/* Last Toggled Info */}
        {settings?.last_toggled_at && (
          <div className="mt-6 text-center text-sm text-gray-400">
            Last toggled: {new Date(settings.last_toggled_at).toLocaleString()}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
